import React, { useState, useEffect } from 'react'
import { InformationCircleIcon } from '@heroicons/react/24/outline'
import axios from 'axios'
import HelpModal from './HelpModal'

interface PageHelpProps {
  pageIdentifier?: string // e.g., 'journal-entries', 'chart-of-accounts'
  articleKey?: string // e.g., 'erp_commission', 'erp_subscription' - maps to KB article titles
  title?: string // Optional custom title for the help article
}

interface KnowledgeArticle {
  id: string
  title: string
  content: string
  excerpt?: string
  category?: {
    id: string
    name: string
  }
  author: {
    firstName: string
    lastName: string
  }
  updatedAt: string
}

// Map articleKey to KB article titles
const articleKeyToTitle: Record<string, string> = {
  'erp_commission': 'Commission Calculation',
  'erp_subscription': 'Subscription Management',
  'erp_payroll': 'Payroll Management',
  'erp_asset': 'Asset Management',
  'erp_project': 'Project Accounting',
  'erp_budget': 'Budgets',
  'erp_tax_exemption': 'Tax Exemptions',
  'erp_tax_group': 'Tax Groups',
  'erp_tax_template': 'Tax Templates',
  'erp_tax_report': 'Tax Reports',
  'erp_profitability': 'Profitability Reports',
  'erp_analytics': 'Financial Analytics Dashboard',
  'erp_print_format': 'Print Formats',
  'erp_email_template': 'Email Templates',
  'erp_document_numbering': 'Document Numbering',
  'erp_opening_balance': 'Opening Balances',
  'erp_payment_allocation': 'Payment Allocation',
  'erp_exchange_rate': 'Exchange Rates',
  'erp_bank_feed': 'Bank Feed Integration',
  'erp_payment_matching': 'Payment Matching',
  'erp_report_template': 'Report Templates',
  'erp_scheduled_report': 'Scheduled Reports',
  'erp_custom_report': 'Custom Report Builder',
  'erp_report_comparison': 'Report Comparison',
  'erp_supplier': 'Suppliers',
  'erp_purchase_order': 'Purchase Orders',
  'erp_grn': 'Goods Receipt Notes (GRN)',
  'erp_recurring_invoice': 'Recurring Invoices',
  'erp_recurring_purchase_invoice': 'Recurring Purchase Invoices',
  'erp_recurring_journal': 'Recurring Journal Entries',
  'erp_payment_reconciliation': 'Payment Reconciliation',
  'erp_bank_reconciliation': 'Bank Reconciliation',
  'erp_data_import': 'Data Import',
  'erp_multi_currency_report': 'Multi-Currency Reports',
}

const PageHelp: React.FC<PageHelpProps> = ({ pageIdentifier, articleKey, title }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [article, setArticle] = useState<KnowledgeArticle | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const fetchHelpArticle = async () => {
    if (isOpen && article) return // Already loaded

    setIsLoading(true)
    try {
      // If articleKey is provided, map it to article title
      const articleTitle = articleKey ? articleKeyToTitle[articleKey] : null
      
      // Search for articles by title, content, or category that matches the page
      // Try multiple search strategies
      const searchTerms = [
        title || articleTitle || pageIdentifier,
        ...(pageIdentifier ? [
          pageIdentifier.replace(/-/g, ' '), // Convert "journal-entries" to "journal entries"
          pageIdentifier.replace(/-/g, ''),  // Convert "journal-entries" to "journalentries"
        ] : []),
        ...(articleKey ? [
          articleKey.replace(/_/g, ' '), // Convert "erp_commission" to "erp commission"
          articleKey.replace('erp_', '').replace(/_/g, ' '), // Convert "erp_commission" to "commission"
        ] : []),
      ].filter(Boolean) as string[]

      let foundArticle: KnowledgeArticle | null = null

      // Try each search term
      for (const searchTerm of searchTerms) {
        const response = await axios.get('/api/knowledge', {
          params: {
            search: searchTerm,
            status: 'published'
          }
        })

        if (response.data.success && response.data.data.length > 0) {
          // Try to find exact match first
          const exactMatch = response.data.data.find(
            (a: KnowledgeArticle) => {
              const titleLower = a.title.toLowerCase()
              const searchLower = searchTerm.toLowerCase()
              return titleLower === searchLower || 
                     titleLower.includes(searchLower) ||
                     searchLower.includes(titleLower)
            }
          )
          
          if (exactMatch) {
            foundArticle = exactMatch
            break
          }
          
          // If no exact match, use first result
          if (!foundArticle) {
            foundArticle = response.data.data[0]
          }
        }
      }

      setArticle(foundArticle)
    } catch (error) {
      console.error('Error fetching help article:', error)
      setArticle(null)
    } finally {
      setIsLoading(false)
    }
  }

  const handleOpen = () => {
    setIsOpen(true)
    fetchHelpArticle()
  }

  return (
    <>
      <button
        onClick={handleOpen}
        className="inline-flex items-center text-gray-400 hover:text-blue-600 transition-colors ml-2"
        title="View help documentation"
        aria-label="View help documentation"
      >
        <InformationCircleIcon className="h-5 w-5" />
      </button>

      <HelpModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        article={article}
        isLoading={isLoading}
        pageIdentifier={pageIdentifier || articleKey || ''}
        pageTitle={title || articleTitle}
      />
    </>
  )
}

export default PageHelp
